export enum TokenType {
    Operator = 'Operator',
    Variable = 'Variable',
    Literal = 'Literal',
    Parenthesis = 'Parenthesis',
}

export class Token {
    constructor(
        public type: TokenType,
        public value: any,
        public position: number,
    ) {}

    get precedence(): number {
        if (this.value === '?') {
            return 20;
        }
        if (['*', '/'].indexOf(this.value) !== -1) {
            return 10;
        }
        if (['+', '-'].indexOf(this.value) !== -1) {
            return 5;
        }

        return -1;
    }
}

function isDigit(c: string) {
    return c >= '0' && c <= '9';
}

function isAlpha(c: string) {
    return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_' || c === '$';
}

function isAlphanumeric(c: string) {
    return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || (c >= '0' && c <= '9') || c === '_' || c === '$';
}

export class Lexer {
    private static readonly OperatorMap: { [key: string]: TokenType } = {
        '*': TokenType.Operator,
        '/': TokenType.Operator,
        '+': TokenType.Operator,
        '-': TokenType.Operator,
        '?': TokenType.Operator,
        '(': TokenType.Parenthesis,
        ')': TokenType.Parenthesis,
    };
    private pos: number = 0;
    private readonly inputLength: number = 0;

    constructor(private readonly input: string) {
        this.inputLength = this.input.length;
    }

    tokenize() {
        const tokens: Token[] = [];
        let token = this.token();
        while (token != null) {
            tokens.push(token);
            token = this.token();
        }

        return tokens;
    }

    private token(): Token | null {
        this.skipNonTokens();
        if (this.pos >= this.inputLength) {
            return null;
        }

        // The char at this.pos is part of a real token. Figure out which.
        const char = this.input.charAt(this.pos);
        const operator = Lexer.OperatorMap[char];

        if (operator) {
            return new Token(operator, char, ++this.pos);
        }

        // Not an operator - so it's the beginning of another token.
        if (isAlpha(char)) {
            return this.processIdentifier();
        }

        if (isDigit(char)) {
            return this.processNumber();
        }

        throw Error(`Invalid token '${char}' at position ${this.pos + 1}.`);
    }

    private processNumber(): Token {
        return this.processToken(TokenType.Literal, isDigit);
    }

    private processIdentifier(): Token {
        return this.processToken(TokenType.Variable, isAlphanumeric);
    }

    private processToken(type: TokenType, checkFn: (c: string) => boolean): Token {
        let end = this.pos + 1;
        while (end < this.inputLength && checkFn(this.input.charAt(end))) {
            end++;
        }

        const token = new Token(type, this.input.substring(this.pos, end), this.pos + 1);
        this.pos = end;
        return token;
    }

    private skipNonTokens() {
        while (this.pos < this.inputLength) {
            const c = this.input.charAt(this.pos);
            if (c == ' ' || c == '\t' || c == '\r' || c == '\n') {
                this.pos++;
            } else {
                break;
            }
        }
    }
}
